import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProject, createBioreactorRun, fetchBioreactorRuns, updateBioreactorRun, uploadBioreactorFile, deleteBioreactorFile, processAggregation, extractExcelValues } from '../services/api';
import { generateCompleteRunsExcel } from '../utils/excelGenerator';
import ExcelViewer from './ExcelViewer';
import DataMapper from './DataMapper';
import ProjectModal from './ProjectModal';
import ThemeToggle from './ThemeToggle';
import AnalyticsDashboard from './AnalyticsDashboard';
import './ProjectLanding.css';

function ProjectLanding({ onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser] = useState('User'); // Default user name

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
    navigate('/login');
  };

  const [activeTab, setActiveTab] = useState('module1');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddDataModal, setShowAddDataModal] = useState(false);
  const [currentRunIndex, setCurrentRunIndex] = useState(1);
  const [totalRuns, setTotalRuns] = useState(10); // Default fallback
  const [savedRuns, setSavedRuns] = useState([]); // Track saved run data
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isSavingRun, setIsSavingRun] = useState(false);
  const [runError, setRunError] = useState('');
  const [editMode, setEditMode] = useState(false); // Track if we're editing an existing run
  const [editingRunId, setEditingRunId] = useState(null); // Track which run we're editing

  // Excel viewer state
  const [showExcelViewer, setShowExcelViewer] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [excelFilename, setExcelFilename] = useState('');

  const [addDataForm, setAddDataForm] = useState({
    runId: '',
    cultureStartDateTime: '',
    cultureEndDateTime: '',
    bioreactorFiles: [], // Changed to array for multiple files
    notes: '',
    runQuality: 'Good Run' // New field for run quality
  });

  // File upload state
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileColumns, setFileColumns] = useState({}); // Store columns for each file
  const [selectedColumns, setSelectedColumns] = useState({}); // Store selected column for each file

  // Data mapper state
  const [showDataMapper, setShowDataMapper] = useState(false);
  const [mappingData, setMappingData] = useState(null);

  // Aggregation state
  const [isProcessingAggregation, setIsProcessingAggregation] = useState(false);
  const [aggregationResult, setAggregationResult] = useState(null);
  const [savedMapping, setSavedMapping] = useState(null);
  const [excelViewerRefreshCallback, setExcelViewerRefreshCallback] = useState(null);

  // Project settings modal state
  const [showProjectModal, setShowProjectModal] = useState(false);

  useEffect(() => {
    // Store current project ID in session storage
    if (projectId) {
      sessionStorage.setItem('currentProjectId', projectId);
    }
    // Load project from API
    loadProject();
    // Load bioreactor runs
    loadBioreactorRuns();

    // Load Datta Able theme assets
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/assets/css/style.css';
    document.head.appendChild(link);

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = '/assets/fonts/tabler-icons.min.css';
    document.head.appendChild(fontLink);

    // Set body attributes for Datta Able theme    document.body.setAttribute('data-pc-preset', 'preset-1');
    document.body.setAttribute('data-pc-sidebar-caption', 'true');
    document.body.setAttribute('data-pc-direction', 'ltr');

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
      if (document.head.contains(fontLink)) {
        document.head.removeChild(fontLink);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Cleanup effect for modal states
  useEffect(() => {
    return () => {
      // Cleanup any open modals when component unmounts
      setShowExcelViewer(false);
      setShowDataMapper(false);
      setShowProjectModal(false);
      setShowAddDataModal(false);
    };
  }, []);

  // Debug logging for projectId
  useEffect(() => {
    console.log('ProjectLanding: Current projectId from useParams:', projectId);
    if (projectId) {
      console.log('ProjectLanding: projectId is available:', projectId);
    } else {
      console.warn('ProjectLanding: projectId is missing or undefined!');
    }
  }, [projectId]);

  // Initialize Bootstrap tooltips for module buttons
  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => {
      // Check if Bootstrap is available
      if (window.bootstrap && window.bootstrap.Tooltip) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      }
      return null;
    });

    // Cleanup function
    return () => {
      tooltipList.forEach(tooltip => {
        if (tooltip && tooltip.dispose) {
          tooltip.dispose();
        }
      });
    };
  }, [activeTab]); // Re-initialize when active tab changes

  // Debug useEffect to monitor addDataForm changes
  useEffect(() => {
    console.log('ProjectLanding: addDataForm changed:', addDataForm);
    console.log('ProjectLanding: addDataForm.bioreactorFiles:', addDataForm.bioreactorFiles);
    console.log('ProjectLanding: addDataForm.bioreactorFiles length:', addDataForm.bioreactorFiles?.length);
  }, [addDataForm]);

  const loadProject = async () => {
    try {
      setIsLoading(true);
      setError('');

      console.log('ProjectLanding: Loading project with ID:', projectId);

      // Check if we have a valid token
      const token = localStorage.getItem('token');
      console.log('ProjectLanding: Token check:', token ? 'Token exists' : 'No token found');

      if (!token) {
        console.warn('ProjectLanding: No authentication token found, redirecting to login');
        navigate('/login');
        return;
      }

      // Load project from API first (primary source)
      try {
        const projectData = await fetchProject(projectId);
        console.log('ProjectLanding: Successfully loaded project from API:', projectData);

        // Map the project data correctly
        const mappedProject = {
          projectId: projectData.projectId || projectId,
          projectName: projectData.projectName || `Project ${projectId.slice(0, 8)}`,
          projectOwner: projectData.projectOwner || 'Unknown Owner',
          scale: projectData.scale || 'Unknown Scale',
          type: projectData.type || 'Unknown Type',
          numberOfRuns: projectData.numberOfRuns || 10,
          createdAt: projectData.createdAt || new Date().toISOString(),
          updatedAt: projectData.updatedAt || new Date().toISOString(),
          userId: projectData.userId || '',
          // Include all other project fields
          ...projectData
        };

        console.log('ProjectLanding: Mapped project data:', mappedProject);
        setProject(mappedProject);
        setTotalRuns(mappedProject.numberOfRuns);

        // Update localStorage with the fresh data from API
        const storedProjects = JSON.parse(localStorage.getItem('projects') || '[]');
        const existingIndex = storedProjects.findIndex(p => p.projectId === projectId);

        if (existingIndex >= 0) {
          storedProjects[existingIndex] = mappedProject;
        } else {
          storedProjects.push(mappedProject);
        }
        localStorage.setItem('projects', JSON.stringify(storedProjects));

      } catch (apiError) {
        console.error('ProjectLanding: API error, trying localStorage fallback:', apiError);

        // Check if it's an authentication error
        if (apiError.message && (apiError.message.includes('401') || apiError.message.includes('Unauthorized') || apiError.message.includes('Could not validate credentials'))) {
          console.warn('ProjectLanding: Authentication failed, redirecting to login');
          localStorage.removeItem('token'); // Clear invalid token
          navigate('/login');
          return;
        }

        // Fallback to localStorage if API fails
        const storedProjects = JSON.parse(localStorage.getItem('projects') || '[]');
        console.log('ProjectLanding: Found', storedProjects.length, 'projects in localStorage');
        const foundProject = storedProjects.find(p => p.projectId === projectId);

        if (foundProject) {
          console.log('ProjectLanding: Found project in localStorage:', foundProject);
          setProject(foundProject);
          setTotalRuns(foundProject.numberOfRuns || 10);
        } else {
          console.warn('ProjectLanding: Project not found in API or localStorage');
          setError(`Project not found. The project with ID "${projectId}" may have been deleted or you may not have access to it.`);
        }
      }
    } catch (error) {
      console.error('ProjectLanding: Unexpected error loading project:', error);
      setError('Failed to load project details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBioreactorRuns = async () => {
    if (!projectId) return;

    try {
      setIsLoadingRuns(true);
      setRunError('');

      console.log('ProjectLanding: Loading bioreactor runs for project:', projectId);
      const runs = await fetchBioreactorRuns(projectId);
      console.log('ProjectLanding: Successfully loaded bioreactor runs:', runs);
      console.log('ProjectLanding: Run details with files:');
      runs.forEach((run, index) => {
        console.log(`Run ${index + 1}:`, {
          runId: run.runId,
          runIndex: run.runIndex,
          bioreactorFiles: run.bioreactorFiles,
          files: run.files, // Check if files are in a different field
          hasFiles: !!(run.bioreactorFiles?.length || run.files?.length),
          fileCount: (run.bioreactorFiles?.length || 0) + (run.files?.length || 0)
        });
      });

      // Deduplicate runs by runId to fix count display
      const uniqueRuns = runs.filter((run, index, self) =>
        index === self.findIndex(r => r.runId === run.runId)
      );
      console.log('ProjectLanding: Deduplicated runs from', runs.length, 'to', uniqueRuns.length);

      setSavedRuns(uniqueRuns);
    } catch (error) {
      console.error('ProjectLanding: Error loading bioreactor runs:', error);
      setRunError('Failed to load bioreactor runs. Using local data.');

      // Fallback to localStorage with deduplication
      const storedRuns = JSON.parse(localStorage.getItem(`bioreactor_runs_${projectId}`) || '[]');
      const uniqueStoredRuns = storedRuns.filter((run, index, self) =>
        index === self.findIndex(r => r.runId === run.runId)
      );
      setSavedRuns(uniqueStoredRuns);
    } finally {
      setIsLoadingRuns(false);
    }
  };
  const handleAddDataClick = () => {
    // Load data for current run index
    console.log('ProjectLanding: handleAddDataClick called with currentRunIndex:', currentRunIndex);
    loadRunData(currentRunIndex);
    setShowAddDataModal(true);

    // Refresh file columns using Data Mapper approach
    setTimeout(() => {
      refreshFileColumns();
    }, 500); // Small delay to ensure modal is open and data is loaded
  };

  const resetForm = () => {
    setCurrentRunIndex(1);
    setAddDataForm({
      runId: '',
      cultureStartDateTime: '',
      cultureEndDateTime: '',
      bioreactorFiles: [],
      notes: '',
      runQuality: 'Good Run'  // Reset to default value
    });
    setEditMode(false);
    setEditingRunId(null);
    // Clear column-related state
    setFileColumns({});
    setSelectedColumns({});
  };

  const handleProcessAggregation = async () => {
    if (!savedMapping) {
      alert('Please create and save data mappings first before proceeding with aggregation.');
      return;
    }

    try {
      setIsProcessingAggregation(true);
      console.log('Starting aggregation process for project:', projectId);

      const result = await processAggregation(projectId);

      console.log('Aggregation completed:', result);
      setAggregationResult(result);

      // Show success message
      alert(`Aggregation completed successfully!\n\nProcessed ${result.processedDates?.length || 0} date ranges.\nAggregation ID: ${result.aggregationId}\n\nUpdated Excel file is ready for viewing.`);

      // Optionally refresh the excel viewer with updated data
      if (result.excelFilePath) {
        // Trigger excel viewer refresh if needed
        console.log('Updated Excel file available at:', result.excelFilePath);
      }

    } catch (error) {
      console.error('Error processing aggregation:', error);
      alert(`Failed to process aggregation: ${error.message}`);
    } finally {
      setIsProcessingAggregation(false);
    }
  };
  // Add Bioreactor Data Modal Handler Functions
  const saveRunData = async () => {
    if (!addDataForm.cultureStartDateTime || !addDataForm.cultureEndDateTime) {
      setRunError('Please fill in both culture start and end date times.');
      throw new Error('Please fill in both culture start and end date times.');
    }

    // Validate for duplicate Run ID before saving
    if (!editMode) {
      const duplicateRun = savedRuns.find(run => run.runId === addDataForm.runId);
      if (duplicateRun) {
        const errorMsg = `Run "${addDataForm.runId}" already exists. Please edit the existing run instead of creating a new one.`;
        setRunError(errorMsg);
        throw new Error(errorMsg);
      }
    }

    try {
      setIsSavingRun(true);
      setRunError('');

      const runData = {
        runId: addDataForm.runId,
        runIndex: currentRunIndex,
        cultureStartDateTime: addDataForm.cultureStartDateTime,
        cultureEndDateTime: addDataForm.cultureEndDateTime,
        bioreactorFiles: addDataForm.bioreactorFiles.map(file => ({
          id: file.id,
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: file.uploadedAt,
          filePath: file.filePath,
          selectedColumn: selectedColumns[file.id] || null
        })),
        notes: addDataForm.notes,
        runQuality: addDataForm.runQuality  // Add the new run quality field
      };

      let savedRun;
      if (editMode && editingRunId) {
        // Update existing run
        savedRun = await updateBioreactorRun(projectId, editingRunId, runData);
        console.log('Run updated successfully:', savedRun);
      } else {
        // Create new run
        savedRun = await createBioreactorRun(projectId, runData);
        console.log('Run created successfully:', savedRun);
      }

      // Update local state immediately for responsive UI
      setSavedRuns(prev => {
        let updated;
        if (editMode) {
          updated = prev.map(run => run.runId === editingRunId ? savedRun : run);
        } else {
          updated = [...prev, savedRun];
        }

        // Deduplicate by runId
        return updated.filter((run, index, self) =>
          index === self.findIndex(r => r.runId === run.runId)
        );
      });

      // Store in localStorage as backup with deduplication
      const updatedRuns = editMode
        ? savedRuns.map(run => run.runId === editingRunId ? savedRun : run)
        : [...savedRuns, savedRun];

      // Deduplicate by runId before storing
      const uniqueRuns = updatedRuns.filter((run, index, self) =>
        index === self.findIndex(r => r.runId === run.runId)
      );
      localStorage.setItem(`bioreactor_runs_${projectId}`, JSON.stringify(uniqueRuns));

      return savedRun;
    } catch (error) {
      console.error('Error saving run:', error);

      // Provide more helpful error messages
      let errorMessage = error.message;
      if (errorMessage.includes("already exists in this project")) {
        errorMessage = `Run "${addDataForm.runId}" already exists in this project. Please use edit mode to modify the existing run, or choose a different Run ID.`;
      } else if (errorMessage.includes("already exists")) {
        errorMessage = `Run "${addDataForm.runId}" already exists in this project. This run may have been saved previously. Please check your saved runs or use edit mode to modify the existing run.`;
      } else if (errorMessage.includes("404")) {
        errorMessage = "Project not found. Please refresh the page and try again.";
      } else if (errorMessage.includes("401") || errorMessage.includes("unauthorized")) {
        errorMessage = "Authentication failed. Please log in again.";
      } else if (!errorMessage || errorMessage === "Failed to fetch") {
        errorMessage = "Unable to connect to server. Please check your internet connection and try again.";
      }

      setRunError(`Failed to save run: ${errorMessage}`);
      throw error;
    } finally {
      setIsSavingRun(false);
    }
  };

  const handleSaveRun = async (e) => {
    e.preventDefault();

    try {
      // Validation function to check for potential issues before saving
      const validateRunData = () => {
        const errors = [];

        // Check required fields
        if (!addDataForm.runId.trim()) {
          errors.push("Run ID is required");
        }

        if (!addDataForm.cultureStartDateTime) {
          errors.push("Culture start date/time is required");
        }

        if (!addDataForm.cultureEndDateTime) {
          errors.push("Culture end date/time is required");
        }

        // Check date logic
        if (addDataForm.cultureStartDateTime && addDataForm.cultureEndDateTime) {
          const startDate = new Date(addDataForm.cultureStartDateTime);
          const endDate = new Date(addDataForm.cultureEndDateTime);

          if (endDate <= startDate) {
            errors.push("Culture end date/time must be after start date/time");
          }
        }

        // Check for duplicate Run ID (only in create mode)
        if (!editMode) {
          const duplicateRun = savedRuns.find(run => run.runId === addDataForm.runId);
          if (duplicateRun) {
            errors.push(`Run ID "${addDataForm.runId}" already exists`);
          }
        }

        return errors;
      };

      // Run validation
      const validationErrors = validateRunData();
      if (validationErrors.length > 0) {
        setRunError(validationErrors.join(' '));
        throw new Error(validationErrors.join(' '));
      }

      await saveRunData();

      // Refresh the runs list from database to ensure consistency
      await loadBioreactorRuns();

      // Reset form and close modal
      resetForm();
      setShowAddDataModal(false);

      // Show success message
      alert(editMode ? 'Run updated successfully!' : 'Run saved successfully!');

    } catch (error) {
      // Error handling is done in saveRunData
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploadingFiles(true);
      setRunError('');

      // Validate that run data is complete before uploading files
      if (!addDataForm.runId.trim()) {
        setRunError('Please enter a Run ID before uploading files.');
        setUploadingFiles(false);
        return;
      }

      if (!addDataForm.cultureStartDateTime || !addDataForm.cultureEndDateTime) {
        setRunError('Please fill in culture start and end date times before uploading files.');
        setUploadingFiles(false);
        return;
      }

      // Check if run exists in database
      const runExists = savedRuns.find(run => run.runId === addDataForm.runId);

      if (!runExists) {
        setRunError('Please save the run first before uploading files. Click "Save Run" button.');
        setUploadingFiles(false);
        return;
      }

      const uploadPromises = files.map(async (file) => {
        try {
          const response = await uploadBioreactorFile(projectId, addDataForm.runId, file);

          // Store column information if available
          if (response.columns && response.columns.length > 0) {
            setFileColumns(prev => ({
              ...prev,
              [response.file.id]: response.columns
            }));
          }

          return {
            id: response.file.id,
            name: response.file.name,
            size: response.file.size,
            type: response.file.type,
            uploadedAt: response.file.uploadedAt,
            filePath: response.file.filePath
          };
        } catch (error) {
          console.error(`Error uploading file ${file.name}:`, error);
          throw new Error(`Failed to upload ${file.name}: ${error.message}`);
        }
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Update form with uploaded files
      setAddDataForm(prev => ({
        ...prev,
        bioreactorFiles: [...prev.bioreactorFiles, ...uploadedFiles]
      }));

      console.log('All files uploaded successfully:', uploadedFiles);

    } catch (error) {
      console.error('Error uploading files:', error);
      setRunError(`Error uploading files: ${error.message}`);
    } finally {
      setUploadingFiles(false);
      // Clear the file input
      e.target.value = '';
    }
  };

  // Function to refresh file columns using Data Mapper approach
  const refreshFileColumns = async () => {
    try {
      console.log('ProjectLanding: Refreshing file columns using Data Mapper approach...');

      // Use the same extractExcelValues API that Data Mapper uses
      const response = await extractExcelValues(projectId, savedRuns);

      console.log('ProjectLanding: extractExcelValues response:', response);

      if (response.success && response.data && response.data.headers) {
        console.log('ProjectLanding: Found headers from backend:', response.data.headers);

        // Apply the headers to all files in the current run
        if (addDataForm.bioreactorFiles && addDataForm.bioreactorFiles.length > 0) {
          const updatedFileColumns = {};
          addDataForm.bioreactorFiles.forEach(file => {
            updatedFileColumns[file.id] = response.data.headers;
          });

          console.log('ProjectLanding: Setting updated file columns:', updatedFileColumns);
          setFileColumns(updatedFileColumns);
        }
      }
    } catch (error) {
      console.error('ProjectLanding: Error refreshing file columns:', error);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteBioreactorFile(projectId, addDataForm.runId, fileId);

      // Remove from form
      setAddDataForm(prev => ({
        ...prev,
        bioreactorFiles: prev.bioreactorFiles.filter(file => file.id !== fileId)
      }));

      // Remove from column state
      setFileColumns(prev => {
        const updated = { ...prev };
        delete updated[fileId];
        return updated;
      });

      setSelectedColumns(prev => {
        const updated = { ...prev };
        delete updated[fileId];
        return updated;
      });

      console.log('File deleted successfully');

    } catch (error) {
      console.error('Error deleting file:', error);
      setRunError(`Failed to delete file: ${error.message}`);
    }
  };

  const handleReplaceFile = async (fileId) => {
    try {
      // Create a hidden file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      input.style.display = 'none';

      input.onchange = async (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
          const file = files[0];

          try {
            setUploadingFiles(true);

            // Delete the old file first
            await deleteBioreactorFile(projectId, addDataForm.runId, fileId);

            // Upload the new file
            const response = await uploadBioreactorFile(projectId, addDataForm.runId, file);

            // Update the form state - replace the old file with the new one
            setAddDataForm(prev => ({
              ...prev,
              bioreactorFiles: prev.bioreactorFiles.map(existingFile =>
                existingFile.id === fileId
                  ? {
                    id: response.file.id,
                    name: response.file.name,
                    size: response.file.size,
                    filePath: response.file.filePath,
                    uploadedAt: response.file.uploadedAt,
                  }
                  : existingFile
              )
            }));

            console.log('File replaced successfully');

          } catch (error) {
            console.error('Error replacing file:', error);
            setRunError(`Failed to replace file: ${error.message}`);
          } finally {
            setUploadingFiles(false);
          }
        }

        // Clean up the input element
        document.body.removeChild(input);
      };

      // Add to body, click, and it will be removed after file selection
      document.body.appendChild(input);
      input.click();

    } catch (error) {
      console.error('Error setting up file replacement:', error);
      setRunError(`Failed to set up file replacement: ${error.message}`);
    }
  };

  const handleColumnSelection = (fileId, columnName) => {
    setSelectedColumns(prev => ({
      ...prev,
      [fileId]: columnName
    }));
  };

  const handlePreviousRun = () => {
    if (currentRunIndex > 1) {
      const newIndex = currentRunIndex - 1;
      setCurrentRunIndex(newIndex);
      loadRunData(newIndex);
    }
  };

  const handleNextRun = () => {
    if (currentRunIndex < totalRuns) {
      const newIndex = currentRunIndex + 1;
      setCurrentRunIndex(newIndex);
      loadRunData(newIndex);
    }
  };

  const handleRunClick = (run) => {
    // Set current run index to the clicked run's index
    setCurrentRunIndex(run.runIndex);

    // Load the run data into form
    setAddDataForm({
      runId: run.runId,
      cultureStartDateTime: run.cultureStartDateTime ? run.cultureStartDateTime.slice(0, 16) : '',
      cultureEndDateTime: run.cultureEndDateTime ? run.cultureEndDateTime.slice(0, 16) : '',
      bioreactorFiles: run.bioreactorFiles || [],
      notes: run.notes || '',
      runQuality: run.runQuality || 'Good Run'  // Load existing run quality or default
    });

    // Set up file columns and selections
    if (run.bioreactorFiles) {
      const columns = {};
      const selections = {};
      run.bioreactorFiles.forEach(file => {
        if (file.columns) {
          columns[file.id] = file.columns;
        }
        if (file.selectedColumn) {
          selections[file.id] = file.selectedColumn;
        }
      });
      setFileColumns(columns);
      setSelectedColumns(selections);
    }

    // Set edit mode
    setEditMode(true);
    setEditingRunId(run.runId);
    setShowAddDataModal(true);
  };
  const handleCompleteAllRuns = async () => {
    // Close current modal
    setShowAddDataModal(false);

    // Show completion message
    const completedRuns = savedRuns.length;
    const totalRunsCount = project?.numberOfRuns || totalRuns;

    if (completedRuns >= totalRunsCount) {
      try {
        // Generate Excel file with all completed runs
        console.log('Generating Excel file for completed runs...');
        const excelResult = generateCompleteRunsExcel(project, savedRuns);

        if (excelResult.success) {
          // Set Excel data for viewer
          setExcelData(excelResult.excelData);
          setExcelFilename(excelResult.filename);

          // Show success message with Excel generation info
          alert(`🎉 Congratulations! All ${totalRunsCount} runs have been completed!\n\nProject: ${project?.projectName || 'Current Project'}\nCompleted Runs: ${completedRuns}/${totalRunsCount}\n\n📊 Excel file "${excelResult.filename}" has been generated with all run data.\n\nOpening Excel Viewer with Aggregation and Imputation capabilities...`);

          // Open Excel viewer
          setShowExcelViewer(true);
        } else {
          // Show completion message without Excel
          alert(`🎉 Congratulations! All ${totalRunsCount} runs have been completed!\n\nProject: ${project?.projectName || 'Current Project'}\nCompleted Runs: ${completedRuns}/${totalRunsCount}\n\n⚠️ Note: Excel file generation failed: ${excelResult.message}\n\nYou can still proceed with data mapping and aggregation.`);
        }
      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert(`🎉 Congratulations! All ${totalRunsCount} runs have been completed!\n\nProject: ${project?.projectName || 'Current Project'}\nCompleted Runs: ${completedRuns}/${totalRunsCount}\n\n⚠️ Note: Excel file generation encountered an error.\n\nYou can still proceed with data mapping and aggregation.`);
      }
    } else {
      const remaining = totalRunsCount - completedRuns;
      alert(`Project Progress Update:\n\nCompleted Runs: ${completedRuns}/${totalRunsCount}\nRemaining Runs: ${remaining}\n\nKeep up the great work! ${remaining} more run${remaining !== 1 ? 's' : ''} to go.`);
    }
  };
  const handleNextRunFromModal = async () => {
    // Save current run first if there are changes
    const hasChanges = addDataForm.cultureStartDateTime || addDataForm.cultureEndDateTime ||
      addDataForm.bioreactorFiles.length > 0 || addDataForm.notes;

    if (hasChanges) {
      // Ask user if they want to save before moving to next
      const shouldSave = window.confirm('You have unsaved changes. Would you like to save this run before moving to the next?');
      if (shouldSave) {
        try {
          // Save current run, then move to next
          await handleSaveRun(new Event('submit'));
          await moveToNextRun();
        } catch (error) {
          console.error('Error saving run before moving to next:', error);
        }
        return;
      }
    }

    await moveToNextRun();
  };
  const moveToNextRun = async () => {
    if (currentRunIndex < (project?.numberOfRuns || totalRuns)) {
      const nextIndex = currentRunIndex + 1;
      setCurrentRunIndex(nextIndex);
      loadRunData(nextIndex);
    } else {
      // Reached the last run - generate Excel and complete
      const completedRuns = savedRuns.length;
      const totalRunsCount = project?.numberOfRuns || totalRuns;

      try {
        // Generate Excel file with all completed runs
        console.log('Generating Excel file for completed runs...');
        const excelResult = generateCompleteRunsExcel(project, savedRuns);

        if (excelResult.success) {
          // Set Excel data for viewer
          setExcelData(excelResult.excelData);
          setExcelFilename(excelResult.filename);

          // Show completion message with Excel generation info
          alert(`🎉 You have reached the last run! All ${totalRunsCount} runs completed!\n\nProject: ${project?.projectName || 'Current Project'}\nCompleted Runs: ${completedRuns}/${totalRunsCount}\n\n📊 Excel file "${excelResult.filename}" has been generated with all run data.\n\nOpening Excel Viewer with Aggregation and Imputation capabilities...`);

          // Close the modal and open Excel viewer
          setShowAddDataModal(false);
          setShowExcelViewer(true);
        } else {
          // Show completion message without Excel
          alert(`🎉 You have reached the last run! All ${totalRunsCount} runs completed!\n\nProject: ${project?.projectName || 'Current Project'}\nCompleted Runs: ${completedRuns}/${totalRunsCount}\n\n⚠️ Note: Excel file generation failed: ${excelResult.message}\n\nYou can still proceed with data mapping and aggregation.`);
          handleCompleteAllRuns();
        }
      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert(`🎉 You have reached the last run! All ${totalRunsCount} runs completed!\n\nProject: ${project?.projectName || 'Current Project'}\nCompleted Runs: ${completedRuns}/${totalRunsCount}\n\n⚠️ Note: Excel file generation encountered an error.\n\nYou can still proceed with data mapping and aggregation.`);
        handleCompleteAllRuns();
      }
    }
  };

  const loadRunData = (runIndex) => {
    // Find existing run data for this index
    const existingRun = savedRuns.find(run => run.runIndex === runIndex);

    console.log('ProjectLanding: loadRunData called with runIndex:', runIndex);
    console.log('ProjectLanding: savedRuns:', savedRuns);
    console.log('ProjectLanding: existingRun found:', existingRun);

    if (existingRun) {
      console.log('ProjectLanding: Full existingRun object:', JSON.stringify(existingRun, null, 2));
      console.log('ProjectLanding: existingRun.bioreactorFiles:', existingRun.bioreactorFiles);
      console.log('ProjectLanding: existingRun.bioreactorFiles type:', typeof existingRun.bioreactorFiles);
      console.log('ProjectLanding: existingRun.bioreactorFiles length:', existingRun.bioreactorFiles?.length);
      // Load existing run data in edit mode
      setAddDataForm({
        runId: existingRun.runId,
        cultureStartDateTime: existingRun.cultureStartDateTime ? existingRun.cultureStartDateTime.slice(0, 16) : '',
        cultureEndDateTime: existingRun.cultureEndDateTime ? existingRun.cultureEndDateTime.slice(0, 16) : '',
        bioreactorFiles: existingRun.bioreactorFiles || [],
        notes: existingRun.notes || '',
        runQuality: existingRun.runQuality || 'Good Run'  // Load existing run quality
      });

      console.log('ProjectLanding: setAddDataForm called with bioreactorFiles:', existingRun.bioreactorFiles || []);

      // Set up file columns and selections
      if (existingRun.bioreactorFiles) {
        console.log('ProjectLanding: Setting up file columns and selections for files:', existingRun.bioreactorFiles);
        const columns = {};
        const selections = {};
        existingRun.bioreactorFiles.forEach(file => {
          console.log('ProjectLanding: Processing file for columns/selections:', file);
          if (file.columns) {
            columns[file.id] = file.columns;
          }
          if (file.selectedColumn) {
            selections[file.id] = file.selectedColumn;
          }
        });
        setFileColumns(columns);
        setSelectedColumns(selections);
        console.log('ProjectLanding: Set fileColumns:', columns);
        console.log('ProjectLanding: Set selectedColumns:', selections);
      } else {
        console.log('ProjectLanding: No bioreactorFiles to set up columns for');
      }

      setEditMode(true);
      setEditingRunId(existingRun.runId);
      setRunError(''); // Clear any previous errors
    } else {
      console.log('ProjectLanding: No existingRun found for runIndex:', runIndex);
      // Check if a run with the auto-generated ID already exists (potential duplicate)
      const autoGeneratedId = `RUN-${String(runIndex).padStart(3, '0')}`;
      console.log('ProjectLanding: Checking for duplicate with autoGeneratedId:', autoGeneratedId);
      const duplicateRun = savedRuns.find(run => run.runId === autoGeneratedId);
      console.log('ProjectLanding: duplicateRun found:', duplicateRun);

      if (duplicateRun) {
        console.log('ProjectLanding: duplicateRun.bioreactorFiles:', duplicateRun.bioreactorFiles);
        // Auto-generated ID conflicts with existing run, load that run instead
        setAddDataForm({
          runId: duplicateRun.runId,
          cultureStartDateTime: duplicateRun.cultureStartDateTime ? duplicateRun.cultureStartDateTime.slice(0, 16) : '',
          cultureEndDateTime: duplicateRun.cultureEndDateTime ? duplicateRun.cultureEndDateTime.slice(0, 16) : '',
          bioreactorFiles: duplicateRun.bioreactorFiles || [],
          notes: duplicateRun.notes || '',
          runQuality: duplicateRun.runQuality || 'Good Run'  // Load existing run quality
        });

        setEditMode(true);
        setEditingRunId(duplicateRun.runId);
        setRunError(`Loading existing run "${autoGeneratedId}" in edit mode.`);
      } else {
        console.log('ProjectLanding: Creating new run data for autoGeneratedId:', autoGeneratedId);
        // Create new run data
        setAddDataForm({
          runId: autoGeneratedId,
          cultureStartDateTime: '',
          cultureEndDateTime: '',
          bioreactorFiles: [],
          notes: '',
          runQuality: 'Good Run'  // Default value for new runs
        });
        setEditMode(false);
        setEditingRunId(null);
        setFileColumns({});
        setSelectedColumns({});
        setRunError(''); // Clear any previous errors
      }
    }
  };

  // Handler for aggregate data functionality
  const handleAggregateData = (dataToAggregate, filename, refreshCallback) => {
    try {      // Prepare mapping data with project and saved runs
      const mappingData = {
        project: project,
        savedRuns: savedRuns,
        dataToAggregate: dataToAggregate,
        filename: filename,
        projectId: projectId
      };

      // Set the mapping data and open the DataMapper modal
      setMappingData(mappingData);
      setShowDataMapper(true);

      // Store the refresh callback for later use
      if (refreshCallback) {
        setExcelViewerRefreshCallback(() => refreshCallback);
      }

      console.log('Aggregate Data handler called:', {
        projectId,
        dataToAggregate: dataToAggregate?.length || 0,
        filename,
        savedRuns: savedRuns?.length || 0
      });
    } catch (error) {
      console.error('Error in handleAggregateData:', error);
      alert('Failed to prepare aggregation data. Please try again.');
    }
  };

  const menuItems = [
    {
      title: 'Add Data',
      description: 'Input and manage project data',
      icon: '📊',
      action: () => console.log('Add Data clicked'),
      color: 'primary'
    },
    {
      title: 'View Results',
      description: 'Analyze project outcomes',
      icon: '📈',
      action: () => console.log('View Results clicked'),
      color: 'success'
    },
    {
      title: 'Process Aggregation',
      description: 'Aggregate bioreactor data with mappings',
      icon: '🔄',
      action: handleProcessAggregation,
      color: 'primary',
      disabled: !savedMapping || isProcessingAggregation
    },
    {
      title: 'Generate Reports',
      description: 'Create detailed reports',
      icon: '📄',
      action: () => console.log('Generate Reports clicked'),
      color: 'info'
    },
    {
      title: 'Project Settings',
      description: 'Configure project parameters',
      icon: '⚙️',
      action: () => console.log('Project Settings clicked'),
      color: 'warning'
    },
    {
      title: 'Export Data',
      description: 'Download project data',
      icon: '💾',
      action: () => console.log('Export Data clicked'),
      color: 'secondary'
    },
    {
      title: 'Collaboration',
      description: 'Share with team members',
      icon: '👥',
      action: () => console.log('Collaboration clicked'),
      color: 'dark'
    }
  ];

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center">
          <h4>Error</h4>
          <p>{error}</p>
          <button className="btn btn-outline-danger" onClick={() => navigate('/main')}>
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Pre-loader */}
      <div className="loader-bg" style={{ display: isLoading ? 'flex' : 'none' }}>
        <div className="loader-track">
          <div className="loader-fill"></div>
        </div>
      </div>

      {/* Header - Full Width with Datta Able styling */}
      <header className="w-100 shadow-sm header-strip position-fixed" style={{ top: 0, left: 0, right: 0, zIndex: 1050, height: '70px' }}>
        <div className="container-fluid px-4 h-100">
          <div className="row align-items-center h-100">
            <div className="col-md-6">              <div className="d-flex align-items-center">
              <img src="/assets/images/RapidCMCLogo.jpeg" alt="RapidCMC" style={{ height: '32px' }} />
              <span className="text-primary ms-2 fw-bold fs-5">RapidCMC</span>
              <span className="text-muted ms-3">
                <i className="ti ti-chevron-right me-1"></i>
                {project?.projectName || 'Loading...'}
              </span>
            </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-end align-items-center">                <button
                className="btn btn-outline-primary btn-sm d-flex align-items-center me-3"
                onClick={() => navigate('/main')}
              >
                <i className="ti ti-arrow-left me-1"></i>
                Back to Projects
              </button>
                <ThemeToggle />
                <div className="dropdown ms-2">
                  <button
                    className="btn btn-light dropdown-toggle d-flex align-items-center"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-user-circle me-2"></i>
                    <span className="d-none d-lg-inline">{currentUser}</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li className="dropdown-header bg-primary text-white">
                      <div className="d-flex align-items-center">
                        <i className="ti ti-user-circle me-2" style={{ fontSize: '1.5rem' }}></i>
                        <div>
                          <h6 className="mb-0">{currentUser}</h6>
                          <small>User</small>
                        </div>
                      </div>
                    </li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => setShowProjectModal(true)}
                      >
                        <i className="ti ti-settings me-2"></i>Project Settings
                      </button>
                    </li>
                    <li><a className="dropdown-item" href="#"><i className="ti ti-user me-2"></i>Profile</a></li>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item text-danger" onClick={handleLogout}>
                        <i className="ti ti-logout me-2"></i>Logout
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Full Width with Datta Able styling */}
      <div className="w-100" style={{ marginTop: '70px', minHeight: 'calc(100vh - 70px)' }}>
        <div className="container-fluid px-4 py-4">
          {/* Project Overview Card - Single Line Modern Design */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card project-overview-card shadow-sm">
                <div className="card-body p-2">
                  <div className="d-flex align-items-center justify-content-between">
                    {/* Left: Project Info - Single Line Layout */}
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="project-icon-modern me-3">
                        <i className="ti ti-clipboard-data"></i>
                      </div>
                      <div className="me-4">
                        <h5 className="project-title-single mb-0">{project?.projectName || 'Project Dashboard'}</h5>
                      </div>
                      <div className="project-metadata-single d-flex align-items-center flex-wrap">
                        <span className="metadata-badge-single">
                          <i className="ti ti-user-circle me-1"></i>
                          {project?.projectOwner || 'Unknown'}
                        </span>
                        <span className="metadata-badge-single">
                          <i className="ti ti-scale me-1"></i>
                          {project?.scale || 'Unknown'}
                        </span>
                        <span className="metadata-badge-single">
                          <i className="ti ti-tag me-1"></i>
                          {project?.type || 'Unknown'}
                        </span>
                        <span className="metadata-badge-single">
                          <i className="ti ti-flask me-1"></i>
                          {project?.numberOfRuns || totalRuns || 'Unknown'} runs
                        </span>
                        <span className="metadata-badge-single">
                          <i className="ti ti-calendar me-1"></i>
                          {project?.createdAt ? new Date(project.createdAt).toLocaleDateString() : 'Unknown'}
                        </span>
                      </div>
                    </div>

                    {/* Right: Status - Single Line */}
                    <div className="ms-3">
                      <span className="status-badge-single active">
                        <i className="ti ti-circle-check me-1"></i>
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>          {/* Main Content Grid - Datta Able style */}
          <div className="row g-4">            {/* Left Sidebar - Enhanced Add Data with Datta Able card styling */}
            <div className={`col-lg-${sidebarCollapsed ? 1 : 3} col-md-${sidebarCollapsed ? 2 : 4} sidebar-col`}>
              <div
                className={`card h-auto border-primary ${sidebarCollapsed ? 'collapsed' : ''}`}
                style={{
                  cursor: sidebarCollapsed ? 'pointer' : 'default',
                  backgroundColor: '#f8f9fa',
                  borderWidth: '2px',
                  boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
                  minHeight: 'auto'
                }}
                onClick={sidebarCollapsed ? () => setSidebarCollapsed(false) : undefined}
              >
                <div className="card-body d-flex flex-column">                  {!sidebarCollapsed && (
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 text-primary rounded p-2 me-2">
                        <i className="ti ti-database"></i>
                      </div>
                      <h5 className="text-dark mb-0 fw-semibold">Data Manager</h5>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-secondary border-0 p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSidebarCollapsed(true);
                      }}
                      title="Collapse Sidebar"
                      style={{ width: '28px', height: '28px' }}
                    >
                      <i className="ti ti-chevron-left"></i>
                    </button>
                  </div>
                )}
                  {sidebarCollapsed ? (
                    <div className="text-center">
                      <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-3 d-inline-flex mb-2">
                        <i className="ti ti-database" style={{ fontSize: '1.5rem' }}></i>
                      </div>
                      <small className="text-muted d-block fw-medium">Data</small>
                    </div>
                  ) : (
                    <>
                      {/* Enhanced Add Data Button */}
                      <button
                        className="btn btn-primary btn-lg mb-4 shadow-sm"
                        onClick={handleAddDataClick}
                        style={{
                          borderRadius: '12px',
                          fontWeight: '600'
                        }}
                      >
                        <i className="ti ti-plus me-2"></i>
                        Bio Process Data
                      </button>
                      {/* Enhanced Progress Stats */}
                      <div className="bg-light border rounded-3 p-3 mb-4">
                        <div className="text-center">
                          <div className="position-relative d-inline-block mb-2">
                            <div
                              className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: '60px', height: '60px' }}
                            >
                              <span className="text-primary fw-bold fs-6">
                                {savedRuns.length}/{project?.numberOfRuns || totalRuns}
                              </span>
                            </div>
                          </div>
                          <div className="text-muted">
                            <small className="d-block fw-medium text-dark">Runs Completed</small>
                            <small className="text-muted">
                              {project?.projectName && project.projectName.length > 20
                                ? `${project.projectName.substring(0, 20)}...`
                                : project?.projectName || 'Loading...'}
                            </small>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="progress mt-3" style={{ height: '6px', backgroundColor: '#e9ecef' }}>
                          <div
                            className="progress-bar-custom-green"
                            style={{
                              width: `${((savedRuns.length / (project?.numberOfRuns || totalRuns)) * 100)}%`,
                              borderRadius: '3px'
                            }}
                          ></div>
                        </div>
                      </div>
                      {/* Enhanced Saved Runs List */}
                      <div className="saved-runs-section flex-grow-1" style={{
                        transition: 'all 0.3s ease',
                        height: 'auto',
                        overflow: 'visible'
                      }}>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <h6 className="text-dark mb-0 fw-semibold d-flex align-items-center">
                            <i className="ti ti-list-check me-2 text-primary"></i>
                            Saved Runs
                          </h6>
                          <span className="badge bg-primary fw-semibold">
                            {savedRuns.length}
                            {savedRuns.length > 5 && (
                              <i className="ti ti-chevron-down ms-1" title="Scroll to see all runs" style={{ fontSize: '0.7rem' }}></i>
                            )}
                          </span>
                        </div>
                        {savedRuns.length === 0 ? (
                          <div className="text-center py-4">
                            <div className="bg-light border rounded-circle p-3 d-inline-flex mb-2">
                              <i className="ti ti-inbox text-muted" style={{ fontSize: '1.5rem' }}></i>
                            </div>
                            <small className="text-muted d-block fw-medium">No runs saved yet</small>
                            <small className="text-muted">Start by adding your first run</small>
                          </div>
                        ) : (
                          <div className="runs-container">
                            {savedRuns.map((run, index) => (<div
                              key={index}
                              className="run-item mb-1 p-2 rounded-3 border position-relative overflow-hidden"
                              style={{
                                cursor: 'pointer',
                                backgroundColor: '#ffffff',
                                borderColor: '#e9ecef',
                                transition: 'all 0.3s ease'
                              }} title={`Saved: ${new Date(run.savedAt || run.createdAt || Date.now()).toLocaleString()}\nClick to view/edit`}
                              onClick={() => handleRunClick(run)}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.borderColor = '#007bff';
                                e.target.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffffff';
                                e.target.style.borderColor = '#e9ecef';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="run-id text-dark fw-semibold">{run.runId}</span>
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 me-2">
                                    <i className="ti ti-check" style={{ fontSize: '0.75rem' }}></i>
                                  </span>
                                </div>
                              </div>
                              <small className="run-details text-muted d-flex align-items-center">
                                <i className="ti ti-file-description me-1 text-primary" style={{ fontSize: '0.75rem' }}></i>
                                {run.bioreactorFiles && run.bioreactorFiles.length > 0
                                  ? `${run.bioreactorFiles.length} file${run.bioreactorFiles.length !== 1 ? 's' : ''} uploaded`
                                  : run.bioreactorFile?.name || 'File uploaded'
                                }
                              </small>
                            </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Enhanced Aggregation Status */}
                      <div className="aggregation-status mt-4 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                        <h6 className="text-dark mb-3 fw-semibold d-flex align-items-center">
                          <i className="ti ti-settings me-2 text-primary"></i>
                          Processing Status
                        </h6>

                        <div className="d-flex flex-column gap-2">
                          {/* Mapping Status */}
                          <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light border">
                            <div className="d-flex align-items-center">
                              <div className={`badge ${savedMapping ? 'bg-success' : 'bg-warning'} bg-opacity-10 me-2`}>
                                <i className={`ti ${savedMapping ? 'ti-check' : 'ti-alert-triangle'} ${savedMapping ? 'text-success' : 'text-warning'}`} style={{ fontSize: '0.75rem' }}></i>
                              </div>
                              <small className="text-dark fw-medium">Mappings</small>
                            </div>
                            <span className={`status-badge ${savedMapping ? 'status-success' : 'status-pending'}`}>
                              {savedMapping ? `${savedMapping.totalMappings || 0} saved` : 'Pending'}
                            </span>
                          </div>

                          {/* Aggregation Status */}
                          <div className="d-flex align-items-center justify-content-between p-2 rounded bg-light border">
                            <div className="d-flex align-items-center">
                              <div className={`badge ${aggregationResult ? 'bg-success' : 'bg-secondary'} bg-opacity-10 me-2`}>
                                <i className={`ti ${aggregationResult ? 'ti-check' : 'ti-circle'} ${aggregationResult ? 'text-success' : 'text-secondary'}`} style={{ fontSize: '0.75rem' }}></i>
                              </div>
                              <small className="text-dark fw-medium">Aggregation</small>
                            </div>
                            <span className={`status-badge ${aggregationResult ? 'status-success' : 'status-pending'}`}>
                              {aggregationResult ? `${aggregationResult.processedDates?.length || 0} processed` : 'Pending'}
                            </span>
                          </div>

                          {isProcessingAggregation && (
                            <div className="d-flex align-items-center p-2 rounded bg-info bg-opacity-10 border border-info border-opacity-25">
                              <div className="spinner-border spinner-border-sm text-info me-2" role="status"></div>
                              <small className="text-info fw-medium">Processing aggregation...</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>            {/* Center Content - Datta Able style */}
            <div className={`col-lg-${sidebarCollapsed ? 11 : 9} col-md-${sidebarCollapsed ? 10 : 8}`}>
              {/* Enhanced Module Navigation - Above Project Modules */}
              <div className="row mb-3">
                <div className="col">
                  <ul className="nav nav-pills enhanced-module-nav-top" role="tablist">
                    <li className="nav-item flex-fill">
                      <button
                        className={`nav-link position-relative enhanced-module-btn ${activeTab === 'module1' ? 'active' : ''}`}
                        onClick={() => setActiveTab('module1')}
                        type="button"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Advanced data visualization and analytics tools for bioreactor performance monitoring"
                      >
                        <i className="ti ti-chart-line me-2"></i>
                        <span className="module-name">Module 1</span>
                      </button>
                    </li>
                    <li className="nav-item flex-fill">
                      <button
                        className={`nav-link position-relative enhanced-module-btn ${activeTab === 'module2' ? 'active' : ''}`}
                        onClick={() => setActiveTab('module2')}
                        type="button"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Comprehensive data management system for processing and organizing bioreactor data"
                      >
                        <i className="ti ti-database me-2"></i>
                        <span className="module-name">Module 2</span>
                      </button>
                    </li>
                    <li className="nav-item flex-fill">
                      <button
                        className={`nav-link position-relative enhanced-module-btn ${activeTab === 'module3' ? 'active' : ''}`}
                        onClick={() => setActiveTab('module3')}
                        type="button"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Process optimization tools for improving bioreactor efficiency and performance"
                      >
                        <i className="ti ti-adjustments me-2"></i>
                        <span className="module-name">Module 3</span>
                      </button>
                    </li>
                    <li className="nav-item flex-fill">
                      <button
                        className={`nav-link position-relative enhanced-module-btn ${activeTab === 'module4' ? 'active' : ''}`}
                        onClick={() => setActiveTab('module4')}
                        type="button"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Comprehensive reporting and analytics for quality management and compliance"
                      >
                        <i className="ti ti-file-analytics me-2"></i>
                        <span className="module-name">Module 4</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Enhanced Module Tabs - Datta Able style */}
              <div className="row mb-4">
                <div className="col">
                  <div className="card shadow-sm border-0">
                    <div className="card-header bg-transparent border-0 pb-0">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h5 className="mb-0 fw-semibold text-dark">
                          <i className="ti ti-layout-grid me-2 text-primary"></i>
                          Project Modules
                        </h5>
                        <div className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                          4 Modules Available
                        </div>
                      </div>
                    </div>

                    <div className="card-body pt-4">
                      <div className="tab-content">
                        <div className={`tab-pane fade ${activeTab === 'module1' ? 'show active' : ''}`}>
                          {/* Analytics Dashboard - Integrated into Module 1 */}
                          <AnalyticsDashboard
                            projectData={project}
                            savedRuns={savedRuns}
                            aggregationResult={aggregationResult}
                          />
                        </div>

                        <div className={`tab-pane fade ${activeTab === 'module2' ? 'show active' : ''}`}>
                          <div className="d-flex align-items-start">
                            <div className="bg-success bg-opacity-10 rounded-3 p-3 me-3">
                              <i className="ti ti-database text-success" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="fw-semibold text-dark mb-2">Data Management</h5>
                              <p className="text-muted mb-3">Comprehensive data storage, processing, and management capabilities for all bioreactor operations.</p>
                              <div className="d-flex gap-2">
                                <span className="badge bg-success bg-opacity-10 text-success">Data Storage</span>
                                <span className="badge bg-warning bg-opacity-10 text-warning">Processing Pipeline</span>
                                <span className="badge bg-danger bg-opacity-10 text-danger">Quality Control</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className={`tab-pane fade ${activeTab === 'module3' ? 'show active' : ''}`}>
                          <div className="d-flex align-items-start">
                            <div className="bg-warning bg-opacity-10 rounded-3 p-3 me-3">
                              <i className="ti ti-settings text-warning" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="fw-semibold text-dark mb-2">Process Optimization</h5>
                              <p className="text-muted mb-3">Intelligent process optimization algorithms and parameter tuning for enhanced bioreactor efficiency.</p>
                              <div className="d-flex gap-2">
                                <span className="badge bg-warning bg-opacity-10 text-warning">Auto-tuning</span>
                                <span className="badge bg-primary bg-opacity-10 text-primary">ML Optimization</span>
                                <span className="badge bg-info bg-opacity-10 text-info">Parameter Control</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className={`tab-pane fade ${activeTab === 'module4' ? 'show active' : ''}`}>
                          <div className="d-flex align-items-start">
                            <div className="bg-info bg-opacity-10 rounded-3 p-3 me-3">
                              <i className="ti ti-file-analytics text-info" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="fw-semibold text-dark mb-2">Reports & Analytics</h5>
                              <p className="text-muted mb-3">Comprehensive reporting suite with automated report generation and advanced analytical insights.</p>
                              <div className="d-flex gap-2">
                                <span className="badge bg-info bg-opacity-10 text-info">Auto Reports</span>
                                <span className="badge bg-secondary bg-opacity-10 text-secondary">Export Options</span>
                                <span className="badge bg-success bg-opacity-10 text-success">KPI Dashboard</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center Action Cards Grid - Datta Able style */}
              <div className="row g-4">
                {menuItems
                  .filter(item => item.title !== 'Add Data' && item.title !== 'Project Settings')
                  .map((item, index) => (
                    <div className="col-lg-6 col-md-6" key={index}>
                      <div
                        className={`card h-100 ${item.disabled ? 'opacity-50' : ''}`}
                        onClick={item.disabled ? undefined : item.action}
                        style={{
                          cursor: item.disabled ? 'not-allowed' : 'pointer',
                          opacity: item.disabled ? 0.6 : 1
                        }}
                      >
                        <div className="card-body d-flex flex-column text-center">
                          <div className="action-icon mb-3" style={{ fontSize: '3rem' }}>
                            {item.icon}
                          </div>
                          <h5 className={`card-title text-${item.color}`}>{item.title}</h5>
                          <div className="card-text flex-grow-1">
                            {item.description}
                            {item.title === 'Process Aggregation' && (
                              <div className="mt-2">
                                {!savedMapping && (
                                  <small className="text-warning d-block">
                                    <i className="ti ti-alert-triangle me-1"></i>
                                    Please create data mappings first
                                  </small>
                                )}
                                {isProcessingAggregation && (
                                  <small className="text-info d-block">
                                    <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                                    Processing...
                                  </small>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            className={`btn btn-outline-${item.color} btn-sm`}
                            disabled={item.disabled}
                          >
                            {item.title === 'Process Aggregation' && isProcessingAggregation ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                                Processing
                              </>
                            ) : (
                              'Open'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Recent Activity - Datta Able style */}
          <div className="row mt-5">
            <div className="col">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Recent Activity</h5>
                </div>
                <div className="card-body">
                  <div className="timeline">
                    <div className="timeline-item d-flex align-items-center mb-3">
                      <div className="timeline-marker bg-primary rounded-circle me-3" style={{ width: '10px', height: '10px' }}></div>
                      <div>
                        <span className="timeline-time text-muted small">Today</span>
                        <div className="timeline-content">Project created</div>
                      </div>
                    </div>
                    <div className="timeline-item d-flex align-items-center">
                      <div className="timeline-marker bg-secondary rounded-circle me-3" style={{ width: '10px', height: '10px' }}></div>
                      <div>
                        <span className="timeline-time text-muted small">-</span>
                        <div className="timeline-content">No additional activity yet</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Placeholder for modals - These will use Bootstrap modal structure */}      {/* Add Data Modal */}
      {showAddDataModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Bio Process Data</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddDataModal(false)}></button>
              </div>

              <div className="modal-body">
                {runError && (
                  <div className={`alert ${runError.includes('Loading existing') ? 'alert-info' : 'alert-danger'}`} role="alert">
                    <div className="d-flex align-items-center">
                      <i className={`ti ${runError.includes('Loading existing') ? 'ti-info-circle' : 'ti-alert-circle'} me-2`}></i>
                      <div>
                        <strong>{runError.includes('Loading existing') ? 'Info:' : 'Error:'}</strong> {runError}
                      </div>
                    </div>
                  </div>
                )}

                {/* Run Progress Timeline */}
                <div className="run-progress-timeline mb-4">
                  <div className="progress-container">
                    <div className="progress-line">
                      <div
                        className="progress-fill"
                        style={{ width: `${(savedRuns.length / totalRuns) * 100}%` }}
                      ></div>
                    </div>
                    <div className="progress-steps">
                      {Array.from({ length: totalRuns }, (_, index) => {
                        const runNumber = index + 1;
                        const isCompleted = runNumber <= savedRuns.length;
                        const isCurrent = runNumber === currentRunIndex && !isCompleted;

                        return (
                          <div
                            key={runNumber}
                            className={`progress-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                            title={`Run ${runNumber} ${isCompleted ? '(Completed)' : isCurrent ? '(Current)' : '(Pending)'}`}
                          >
                            <div className="step-circle">
                              {isCompleted ? (
                                <i className="ti ti-check"></i>
                              ) : (
                                <span>{runNumber}</span>
                              )}
                            </div>
                            <div className="step-label">Run {runNumber}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSaveRun}>
                  {/* Run Navigation */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handlePreviousRun}
                            disabled={currentRunIndex <= 1}
                          >
                            <i className="ti ti-chevron-left"></i> Previous
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleNextRun}
                            disabled={currentRunIndex >= totalRuns}
                          >
                            Next <i className="ti ti-chevron-right"></i>
                          </button>
                        </div>
                        <div className="text-muted">
                          Run {currentRunIndex} of {totalRuns}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Run ID Display */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-medium">Run ID</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={addDataForm.runId}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium">Status</label>
                      <div className="form-control bg-light">
                        {editMode ? (
                          <span className="badge bg-warning me-1">
                            <i className="ti ti-edit me-1"></i>Editing Existing Run
                          </span>
                        ) : (
                          <span className="badge bg-success me-1">
                            <i className="ti ti-plus me-1"></i>Creating New Run
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Culture Date Times */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-medium">Culture Start DateTime</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={addDataForm.cultureStartDateTime}
                        onChange={(e) => setAddDataForm(prev => ({
                          ...prev,
                          cultureStartDateTime: e.target.value
                        }))}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-medium">Culture End DateTime</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={addDataForm.cultureEndDateTime}
                        onChange={(e) => setAddDataForm(prev => ({
                          ...prev,
                          cultureEndDateTime: e.target.value
                        }))}
                        required
                      />
                    </div>
                  </div>

                  {/* Run Quality Section */}
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label fw-medium">Run Quality</label>
                      <div className="d-flex flex-wrap gap-3 mt-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="runQuality"
                            id="goodRun"
                            value="Good Run"
                            checked={addDataForm.runQuality === "Good Run"}
                            onChange={(e) => setAddDataForm(prev => ({
                              ...prev,
                              runQuality: e.target.value
                            }))}
                          />
                          <label className="form-check-label d-flex align-items-center" htmlFor="goodRun">
                            <span className="badge bg-success me-2">✓</span>
                            Good Run
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="runQuality"
                            id="badRun"
                            value="Bad Run"
                            checked={addDataForm.runQuality === "Bad Run"}
                            onChange={(e) => setAddDataForm(prev => ({
                              ...prev,
                              runQuality: e.target.value
                            }))}
                          />
                          <label className="form-check-label d-flex align-items-center" htmlFor="badRun">
                            <span className="badge bg-danger me-2">✗</span>
                            Bad Run
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className="mb-4">
                    <label className="form-label fw-medium">Upload Bioreactor Files</label>
                    <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                      <input
                        type="file"
                        className="form-control mb-3"
                        accept=".xlsx,.xls,.csv"
                        multiple
                        onChange={handleFileUpload}
                        disabled={uploadingFiles}
                      />

                      {uploadingFiles && (
                        <div className="d-flex align-items-center text-primary mb-2">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          <small>Uploading files...</small>
                        </div>
                      )}

                      {/* Display uploaded files */}
                      {console.log('ProjectLanding: addDataForm.bioreactorFiles:', addDataForm.bioreactorFiles)}
                      {console.log('ProjectLanding: bioreactorFiles length:', addDataForm.bioreactorFiles?.length)}
                      {addDataForm.bioreactorFiles.length > 0 && (
                        <div className="uploaded-files">
                          <h6 className="text-dark mb-3">Uploaded Files:</h6>
                          {addDataForm.bioreactorFiles.map((file, index) => (
                            <div key={index} className="card mb-2">
                              <div className="card-body py-2">
                                <div className="row align-items-center">
                                  <div className="col-md-8">
                                    <div className="d-flex align-items-center">
                                      <i className="ti ti-file-spreadsheet text-success me-2"></i>
                                      <div>
                                        <span className="fw-medium">{file.name}</span>
                                        <br />
                                        <small className="text-muted">
                                          {(file.size / 1024).toFixed(1)} KB • Uploaded: {
                                            file.uploadedAt
                                              ? new Date(file.uploadedAt).toLocaleDateString()
                                              : 'Unknown'
                                          }
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-4 text-end">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm me-2"
                                      onClick={() => handleReplaceFile(file.id)}
                                      title="Replace file"
                                    >
                                      <i className="ti ti-upload"></i> Replace
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleDeleteFile(file.id)}
                                      title="Delete file"
                                    >
                                      <i className="ti ti-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Show message when no files are uploaded */}
                      {addDataForm.bioreactorFiles.length === 0 && (
                        <div className="no-files-message">
                          <div className="text-center py-4">
                            <i className="ti ti-file-upload text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                            <h6 className="text-muted">No files uploaded yet</h6>
                            <small className="text-muted">
                              Use the "Upload Excel File" button above to add bioreactor data files
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="mb-4">
                    <label className="form-label fw-medium">Notes</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={addDataForm.notes}
                      onChange={(e) => setAddDataForm(prev => ({
                        ...prev,
                        notes: e.target.value
                      }))}
                      placeholder="Add any relevant notes about this bioreactor run..."
                    ></textarea>
                  </div>                  {/* Action Buttons */}
                  <div className="d-flex justify-content-between">
                    <div className="d-flex gap-2">
                      {/* Secondary Action Buttons */}
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={handleNextRunFromModal}
                        disabled={isSavingRun || currentRunIndex >= (project?.numberOfRuns || totalRuns)}
                      >
                        <i className="ti ti-arrow-right me-1"></i>
                        Next Run
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleCompleteAllRuns}
                        disabled={isSavingRun}
                      >
                        <i className="ti ti-check-circle me-1"></i>
                        Complete All Runs
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      {/* Primary Action Buttons */}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowAddDataModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSavingRun}
                      >
                        {isSavingRun ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-device-floppy me-1"></i>
                            {editMode ? 'Update Run' : 'Save Run'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}      {/* Excel Viewer Modal */}
      <ExcelViewer
        show={showExcelViewer}
        onHide={() => setShowExcelViewer(false)}
        excelData={excelData}
        filename={excelFilename}
        onAggregateData={handleAggregateData}
        projectId={projectId}
        projectData={project}
      />

      {/* Data Mapper Modal */}
      <DataMapper
        show={showDataMapper}
        onHide={() => setShowDataMapper(false)}
        mappingData={mappingData}
        projectId={projectId}
      />

      {/* Project Settings Modal */}
      <ProjectModal
        show={showProjectModal}
        onHide={() => setShowProjectModal(false)}
        readOnlyMode={true}
        existingProject={project}
      />
    </>
  );
}

export default ProjectLanding;
